import { useEffect } from 'react';
import { useSoftKeyboard } from '../contexts/SoftKeyboardContext';
import type { Page } from '../types';
import { isPublicGuestOrderingRoute } from '../lib/publicGuestRoutes';

/**
 * Hook to automatically attach soft keyboard to all input fields.
 * Call this once in your app root or layout component.
 * Pass currentPage to disable soft keyboard on specific pages (e.g. 'pos-mobile', report pages).
 */
export const useAutoSoftKeyboard = (currentPage?: Page) => {
  const { enabled, openKeyboard, closeKeyboard } = useSoftKeyboard();

  useEffect(() => {
    if (isPublicGuestOrderingRoute()) {
      closeKeyboard();
    }
  }, [closeKeyboard]);

  useEffect(() => {
    if (!enabled) return;

    // Public online / event booking — native device keyboard only
    if (isPublicGuestOrderingRoute()) return;

    // Do not use soft keyboard on Mobile POS – use native device keyboard
    if (currentPage === 'pos-mobile') return;

    // Reports / detailed financial views: use native keyboard for filters, expense grids, and manual entry
    const reportPages: Page[] = [
      'reports',
      'reports-sales-insights',
      'reports-event-stall',
      'reports-monthly',
      'reports-financial',
      'reports-reconciliation',
      'reports-food-cost',
      'invoices',
      'purchasing',
      'assets',
    ];
    if (currentPage && reportPages.includes(currentPage)) return;

    const handleInputFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;

      if (isPublicGuestOrderingRoute()) return;
      const isInputElement = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' ||
        (target.hasAttribute && target.hasAttribute('contenteditable'));
      
      if (!isInputElement) {
        return;
      }

      const inputElement = target as HTMLInputElement | HTMLTextAreaElement;
      
      // Skip if disabled or readonly
      if (inputElement.hasAttribute('disabled') || inputElement.hasAttribute('readonly')) {
        return;
      }

      // Skip if it's already handled by explicit soft keyboard integration
      if (inputElement.hasAttribute('data-soft-keyboard-handled')) {
        return;
      }

      // Skip hidden inputs, file inputs, checkbox, radio, button, submit, reset
      if (inputElement.type === 'hidden' || 
          inputElement.type === 'file' || 
          inputElement.type === 'checkbox' || 
          inputElement.type === 'radio' || 
          inputElement.type === 'button' || 
          inputElement.type === 'submit' || 
          inputElement.type === 'reset' ||
          inputElement.type === 'image') {
        return;
      }

      // Skip date and time inputs so native calendar/time picker is used app-wide
      if (inputElement.type === 'date' || inputElement.type === 'time' || inputElement.type === 'datetime-local') {
        return;
      }

      // Skip password fields that should use numeric keyboard (PINs)
      if (inputElement.type === 'password' && inputElement.inputMode === 'numeric') {
        openKeyboard({
          id: `auto-${inputElement.id || inputElement.name || 'input'}-${Date.now()}`,
          type: 'numeric',
          value: inputElement.value,
          label: inputElement.placeholder || inputElement.getAttribute('aria-label') || 'Enter value',
          onChange: (value) => {
            // Use native setter so React controlled inputs receive the update
            const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
            if (nativeSetter) nativeSetter.call(inputElement, value);
            else (inputElement as HTMLInputElement).value = value;
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          },
          onSubmit: () => {
            // Blur the input and try to submit form if it exists
            inputElement.blur();
            if (inputElement.form) {
              const submitButton = inputElement.form.querySelector('button[type="submit"], input[type="submit"]') as HTMLButtonElement | HTMLInputElement;
              if (submitButton && !submitButton.disabled) {
                inputElement.form.requestSubmit();
              }
            }
          },
          maskValue: true,
        });
        inputElement.setAttribute('data-soft-keyboard-handled', 'true');
        return;
      }

      // Determine keyboard type: numeric for numbers, alphabetic for names (letters only, smaller), alphanumeric for search/email
      let keyboardType: 'numeric' | 'alphanumeric' | 'alphabetic' = 'alphabetic';
      
      if (inputElement.type === 'number' || 
          inputElement.inputMode === 'numeric' || 
          inputElement.inputMode === 'decimal') {
        keyboardType = 'numeric';
      } else if (inputElement.type === 'search' || 
          inputElement.type === 'email' || 
          inputElement.type === 'url') {
        keyboardType = 'alphanumeric';
      } else if (inputElement.type === 'text' || inputElement.type === 'tel') {
        // tel with inputMode numeric = numeric; otherwise alphabetic for names
        if (inputElement.inputMode === 'numeric' || inputElement.inputMode === 'decimal') {
          keyboardType = 'numeric';
        } else {
          keyboardType = 'alphabetic';
        }
      }

      // For textareas, we need to handle React state updates differently
      const isTextarea = inputElement.tagName === 'TEXTAREA';
      
      openKeyboard({
        id: `auto-${inputElement.id || inputElement.name || 'input'}-${Date.now()}`,
        type: keyboardType,
        value: inputElement.value,
        label: inputElement.placeholder || inputElement.getAttribute('aria-label') || 'Enter value',
        onChange: (value) => {
          // Use native setter so React controlled inputs receive the update (direct .value is ignored)
          const proto = isTextarea ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
          const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
          if (nativeSetter) nativeSetter.call(inputElement, value);
          else (inputElement as HTMLInputElement | HTMLTextAreaElement).value = value;
          inputElement.dispatchEvent(new Event('input', { bubbles: true }));
          inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        },
        onSubmit: () => {
          // Blur the input and try to submit form if it exists
          inputElement.blur();
          if (inputElement.form) {
            const submitButton = inputElement.form.querySelector('button[type="submit"], input[type="submit"]') as HTMLButtonElement | HTMLInputElement;
            if (submitButton && !submitButton.disabled) {
              inputElement.form.requestSubmit();
            }
          }
        },
        maskValue: inputElement.type === 'password',
      });

      inputElement.setAttribute('data-soft-keyboard-handled', 'true');
    };

    // Add event listeners to all current and future inputs
    document.addEventListener('focusin', handleInputFocus as EventListener);

    return () => {
      document.removeEventListener('focusin', handleInputFocus as EventListener);
    };
  }, [enabled, openKeyboard, closeKeyboard, currentPage]);
};
